import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchBenefitsLatest,
  type BenefitFilter,
  type BenefitItem,
} from '#/domains/preview/api/govBenefitApi';

type FilterState = {
  page: number;
  totalPages: number;
  totalElements: number;
};

type State = {
  items: BenefitItem[];
  perFilter: FilterState[];
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
};

const EMPTY: FilterState = { page: 0, totalPages: 0, totalElements: 0 };

const INITIAL_STATE: State = {
  items: [],
  perFilter: [],
  isInitialLoading: true,
  isLoadingMore: false,
  error: null,
};

function dedupeMerge(
  existing: BenefitItem[],
  incoming: BenefitItem[]
): BenefitItem[] {
  if (existing.length === 0) return incoming;
  const seen = new Set(existing.map(b => b.benefitId));
  const merged = [...existing];
  for (const b of incoming) {
    if (!seen.has(b.benefitId)) {
      seen.add(b.benefitId);
      merged.push(b);
    }
  }
  return merged;
}

function buildFilters(
  categoryCodes: string[],
  regions: string[],
  keyword: string
): BenefitFilter[] {
  const cats: (string | null)[] =
    categoryCodes.length === 0 ? [null] : categoryCodes;
  const regs: (string | null)[] = regions.length === 0 ? [null] : regions;
  const k = keyword.trim() || null;
  const out: BenefitFilter[] = [];
  for (const c of cats) {
    for (const r of regs) {
      out.push({ categoryCode: c, region: r, keyword: k });
    }
  }
  return out;
}

export function useBenefitsLatest(
  categoryCodes: string[],
  regions: string[],
  keyword: string
) {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const inFlightRef = useRef<AbortController | null>(null);

  const trimmedKeyword = keyword.trim();

  const filtersKey = useMemo(() => {
    const c = [...categoryCodes].sort().join(',');
    const r = [...regions].sort().join(',');
    return `${c}|${r}|${trimmedKeyword}`;
  }, [categoryCodes, regions, trimmedKeyword]);

  const loadPage = useCallback(
    async (nextPage: number, codes: string[], regs: string[], kw: string) => {
      inFlightRef.current?.abort();
      const controller = new AbortController();
      inFlightRef.current = controller;

      setState(prev => ({
        ...prev,
        isInitialLoading: nextPage === 1,
        isLoadingMore: nextPage > 1,
        error: null,
      }));

      const filters = buildFilters(codes, regs, kw);

      try {
        const results = await Promise.all(
          filters.map(f => fetchBenefitsLatest(nextPage, f, controller.signal))
        );

        setState(prev => {
          const mergedIncoming = results.flatMap(r => r.benefits);
          const perFilter: FilterState[] = results.map(r => ({
            page: r.currentPage,
            totalPages: r.totalPages,
            totalElements: r.totalElements,
          }));
          return {
            items:
              nextPage === 1
                ? dedupeMerge([], mergedIncoming)
                : dedupeMerge(prev.items, mergedIncoming),
            perFilter,
            isInitialLoading: false,
            isLoadingMore: false,
            error: null,
          };
        });
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setState(prev => ({
          ...prev,
          isInitialLoading: false,
          isLoadingMore: false,
          error:
            err instanceof Error
              ? err.message
              : '혜택을 불러오지 못했어요. 잠시 후 다시 시도해주세요.',
        }));
      }
    },
    []
  );

  useEffect(() => {
    setState(INITIAL_STATE);
    const [c, r, k] = filtersKey.split('|');
    loadPage(
      1,
      c === '' ? [] : c.split(','),
      r === '' ? [] : r.split(','),
      k ?? ''
    );
    return () => inFlightRef.current?.abort();
  }, [loadPage, filtersKey]);

  const totals = useMemo(() => {
    if (state.perFilter.length === 0) {
      return {
        currentPage: EMPTY.page,
        totalPages: EMPTY.totalPages,
        totalElements: EMPTY.totalElements,
      };
    }
    let currentPage = Infinity;
    let totalPages = 0;
    let totalElements = 0;
    for (const p of state.perFilter) {
      currentPage = Math.min(currentPage, p.page);
      totalPages = Math.max(totalPages, p.totalPages);
      totalElements += p.totalElements;
    }
    if (!Number.isFinite(currentPage)) currentPage = 0;
    return { currentPage, totalPages, totalElements };
  }, [state.perFilter]);

  const loadMore = useCallback(() => {
    if (state.isLoadingMore || state.isInitialLoading) return;
    if (totals.currentPage >= totals.totalPages) return;
    const [c, r, k] = filtersKey.split('|');
    loadPage(
      totals.currentPage + 1,
      c === '' ? [] : c.split(','),
      r === '' ? [] : r.split(','),
      k ?? ''
    );
  }, [
    loadPage,
    filtersKey,
    totals.currentPage,
    totals.totalPages,
    state.isInitialLoading,
    state.isLoadingMore,
  ]);

  const retry = useCallback(() => {
    const [c, r, k] = filtersKey.split('|');
    loadPage(
      totals.currentPage === 0 ? 1 : totals.currentPage,
      c === '' ? [] : c.split(','),
      r === '' ? [] : r.split(','),
      k ?? ''
    );
  }, [loadPage, filtersKey, totals.currentPage]);

  return {
    items: state.items,
    totalElements: totals.totalElements,
    hasMore: totals.currentPage < totals.totalPages,
    isInitialLoading: state.isInitialLoading,
    isLoadingMore: state.isLoadingMore,
    error: state.error,
    loadMore,
    retry,
  };
}
